/**
 * Token Manager - Centralized token management utility
 * Handles secure storage, retrieval, and validation of authentication tokens
 * Used across AuthService and AuthContext for consistency
 */

import config from '../config';

export interface TokenData {
  token: string;
  refreshToken?: string;
  expiresAt?: number;
}

export class TokenManager {
  /**
   * Store authentication tokens securely
   */
  static storeTokens(tokens: TokenData): void {
    localStorage.setItem(config.AUTH.TOKEN_STORAGE_KEY, tokens.token);
    
    if (tokens.refreshToken) {
      localStorage.setItem(config.AUTH.REFRESH_TOKEN_STORAGE_KEY, tokens.refreshToken);
    }
    
    if (tokens.expiresAt) {
      localStorage.setItem(config.AUTH.EXPIRES_AT_STORAGE_KEY, tokens.expiresAt.toString());
    }
  }

  /**
   * Get stored authentication token
   */
  static getToken(): string | null {
    return localStorage.getItem(config.AUTH.TOKEN_STORAGE_KEY);
  }

  /**
   * Get stored refresh token
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem(config.AUTH.REFRESH_TOKEN_STORAGE_KEY);
  }

  /**
   * Get token expiration time
   */
  static getTokenExpiration(): number | null {
    const expiresAt = localStorage.getItem(config.AUTH.EXPIRES_AT_STORAGE_KEY);
    return expiresAt ? parseInt(expiresAt) : null;
  }

  /**
   * Check if token exists and is not expired
   */
  static isTokenValid(): boolean {
    const token = TokenManager.getToken();
    if (!token) return false;

    const expiresAt = TokenManager.getTokenExpiration();
    if (expiresAt && Date.now() > expiresAt) {
      return false;
    }

    return true;
  }

  /**
   * Check if token will expire soon (within buffer time)
   */
  static isTokenExpiringSoon(): boolean {
    const expiresAt = TokenManager.getTokenExpiration();
    if (!expiresAt) return false;

    return Date.now() > (expiresAt - config.AUTH.TOKEN_REFRESH_BUFFER);
  }

  /**
   * Clear all stored authentication tokens
   */
  static clearTokens(): void {
    localStorage.removeItem(config.AUTH.TOKEN_STORAGE_KEY);
    localStorage.removeItem(config.AUTH.EXPIRES_AT_STORAGE_KEY);
    localStorage.removeItem(config.AUTH.REFRESH_TOKEN_STORAGE_KEY);
  }

  /**
   * Get all token data
   */
  static getAllTokens(): TokenData | null {
    const token = TokenManager.getToken();
    if (!token) return null;

    return {
      token,
      refreshToken: TokenManager.getRefreshToken() || undefined,
      expiresAt: TokenManager.getTokenExpiration() || undefined,
    };
  }
}

/**
 * User data management utilities
 */
export class UserDataManager {
  /**
   * Store user data
   */
  static storeUser(user: any): void {
    localStorage.setItem(config.AUTH.USER_STORAGE_KEY, JSON.stringify(user));
  }

  /**
   * Get stored user data
   */
  static getUser(): any | null {
    const userStr = localStorage.getItem(config.AUTH.USER_STORAGE_KEY);
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Clear stored user data
   */
  static clearUser(): void {
    localStorage.removeItem(config.AUTH.USER_STORAGE_KEY);
  }

  /**
   * Update stored user data
   */
  static updateUser(updates: Partial<any>): void {
    const currentUser = UserDataManager.getUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...updates };
      UserDataManager.storeUser(updatedUser);
    }
  }
}

/**
 * Application preferences management utilities
 */
export class PreferencesManager {
  private static readonly TARGET_TYPE_KEY = 'promptshare_target_type';

  static storeTargetType(type: 'website' | 'app'): void {
    localStorage.setItem(PreferencesManager.TARGET_TYPE_KEY, type);
  }

  static getTargetType(): 'website' | 'app' {
    const type = localStorage.getItem(PreferencesManager.TARGET_TYPE_KEY);
    return (type === 'app') ? 'app' : 'website';
  }
}

// Export convenience functions
export const {
  storeTokens,
  getToken,
  getRefreshToken,
  getTokenExpiration,
  isTokenValid,
  isTokenExpiringSoon,
  clearTokens,
  getAllTokens,
} = TokenManager;

export const {
  storeUser,
  getUser,
  clearUser,
  updateUser,
} = UserDataManager;

export const {
  storeTargetType,
  getTargetType,
} = PreferencesManager;